import React, { useState } from 'react';
import axios from 'axios';
import { Lock, Send, Eye, EyeOff, Archive, RotateCcw } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { TicketStatusBadge, TicketPriorityBadge } from './StatusBadge';
import { AttachmentUploader, renderAttachmentPreview } from './AttachmentUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export function AdminTicketDetail({ ticket, onClose, onUpdated }) {
  const [replyBody, setReplyBody] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [tagsInput, setTagsInput] = useState(ticket?.tags?.join(', ') || '');
  const [showPrivateValues, setShowPrivateValues] = useState(false);

  if (!ticket) return null;

  const updateTicket = async (patch) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/tickets/${ticket.id}`, patch, { headers: getHeaders() });
      onUpdated();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update ticket');
    }
  };

  const sendReply = async () => {
    if (!replyBody.trim() && attachments.length === 0) return;
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/tickets/${ticket.id}/messages`, {
        body: replyBody.trim(), attachments, is_private: isPrivate,
      }, { headers: getHeaders() });
      setReplyBody(''); setAttachments([]); setIsPrivate(false);
      onUpdated();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const toggleArchive = async () => {
    if (ticket.status === 'archived') {
      await updateTicket({ status: 'open' });
    } else {
      await axios.post(`${BACKEND_URL}/api/admin/tickets/${ticket.id}/close`, {}, { headers: getHeaders() });
      onUpdated();
    }
  };

  return (
    <Sheet open={!!ticket} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="admin-ticket-detail-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 flex-wrap">
            <span>#{ticket.ticket_number} &mdash; {ticket.subject}</span>
            <TicketStatusBadge status={ticket.status} />
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          <p className="text-sm text-slate-500">
            {ticket.customer_name} &lt;{ticket.customer_email}&gt;
          </p>

          {/* Controls */}
          <div className="grid grid-cols-3 gap-2">
            <Select value={ticket.status} onValueChange={(v) => updateTicket({ status: v })}>
              <SelectTrigger data-testid="admin-ticket-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="waiting">Waiting</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Select value={ticket.priority} onValueChange={(v) => updateTicket({ priority: v })}>
              <SelectTrigger data-testid="admin-ticket-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={toggleArchive} data-testid="admin-toggle-archive-btn">
              {ticket.status === 'archived' ? <><RotateCcw className="w-3.5 h-3.5 mr-1.5" />Reopen</> : <><Archive className="w-3.5 h-3.5 mr-1.5" />Close</>}
            </Button>
          </div>

          <div className="flex gap-2">
            <Input
              placeholder="Tags (comma separated)"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              onBlur={() => updateTicket({ tags: tagsInput.split(',').map((t) => t.trim()).filter(Boolean) })}
              data-testid="admin-ticket-tags-input"
            />
          </div>

          {/* Private fields (decrypted for admin) */}
          <div className="border rounded-lg p-3 bg-amber-50/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> Private access fields
              </span>
              {ticket.private_fields?.length > 0 && (
                <button onClick={() => setShowPrivateValues((v) => !v)} className="text-xs text-[#6e2ea8] flex items-center gap-1" data-testid="toggle-reveal-private-fields">
                  {showPrivateValues ? <><EyeOff className="w-3.5 h-3.5" />Hide</> : <><Eye className="w-3.5 h-3.5" />Reveal</>}
                </button>
              )}
            </div>
            {(ticket.private_fields || []).length === 0 ? (
              <p className="text-xs text-slate-500">None submitted by customer.</p>
            ) : (
              <ul className="text-sm space-y-1" data-testid="admin-private-fields-list">
                {ticket.private_fields.map((f) => (
                  <li key={f.id} className="flex justify-between">
                    <span className="text-slate-600">{f.label}:</span>
                    <span className="font-mono text-slate-800">{showPrivateValues ? f.value : '••••••••'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Message thread */}
          <div className="space-y-3">
            {(ticket.messages || []).map((m) => (
              <div
                key={m.id}
                className={`p-3 rounded-xl ${m.is_private ? 'bg-yellow-50 border border-yellow-200' : m.sender_type === 'admin' ? 'bg-[#6e2ea8]/5 ml-6' : 'bg-slate-100 mr-6'}`}
                data-testid={`admin-ticket-message-${m.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-slate-700">
                    {m.sender_name} {m.is_private && <span className="text-yellow-600">(internal note)</span>}
                  </span>
                  <span className="text-xs text-slate-400">{new Date(m.created_at).toLocaleString()}</span>
                </div>
                {(m.attachments || []).map((a, i) => renderAttachmentPreview(a, i))}
                {m.body && <p className="text-sm text-slate-700 whitespace-pre-wrap">{m.body}</p>}
              </div>
            ))}
          </div>

          {/* Reply box */}
          <div className="border-t pt-4">
            <Textarea
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              placeholder={isPrivate ? 'Internal note (not visible to customer)...' : 'Reply to customer or paste a screenshot...'}
              rows={3}
              data-testid="admin-ticket-reply-input"
            />
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-3">
                <AttachmentUploader
                  uploadEndpoint={`/api/admin/tickets/${ticket.id}/attachments`}
                  token={localStorage.getItem('token')}
                  attachments={attachments}
                  setAttachments={setAttachments}
                  testIdPrefix="admin-ticket-reply"
                />
                <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                  <Checkbox checked={isPrivate} onCheckedChange={setIsPrivate} data-testid="admin-internal-note-checkbox" />
                  Internal note
                </label>
              </div>
              <Button size="sm" onClick={sendReply} disabled={sending} className="bg-[#6e2ea8] hover:bg-[#5a2690]" data-testid="admin-send-reply-btn">
                <Send className="w-4 h-4 mr-1" /> {sending ? 'Sending...' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
